import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const COMPARE_FIELDS = [
  { key: 'name',          label: 'Name' },
  { key: 'address',       label: 'Address' },
  { key: 'phone',         label: 'Phone' },
  { key: 'website',       label: 'Website' },
  { key: 'type',          label: 'Type' },
  { key: 'tags',          label: 'Tags' },
  { key: 'languages',     label: 'Languages' },
  { key: 'telehealth',    label: 'Telehealth' },
  { key: 'er_24_7',       label: 'ER 24/7' },
  { key: 'last_verified', label: 'Last verified' },
];

const ALL_FIELDS = [
  'name','country_code','country','city','address','phone','type','tags',
  'website','booking_url','specialties_url','telehealth','er_24_7',
  'languages','latitude','longitude','last_verified','notes',
];

const MERGE_FIELDS = [
  { key: 'name',           label: 'Name' },
  { key: 'address',        label: 'Address' },
  { key: 'city',           label: 'City' },
  { key: 'phone',          label: 'Phone' },
  { key: 'website',        label: 'Website' },
  { key: 'type',           label: 'Type' },
  { key: 'tags',           label: 'Tags' },
  { key: 'languages',      label: 'Languages' },
  { key: 'booking_url',    label: 'Booking URL' },
  { key: 'telehealth',     label: 'Telehealth' },
  { key: 'er_24_7',        label: 'ER 24/7' },
  { key: 'last_verified',  label: 'Last verified' },
  { key: 'notes',          label: 'Notes' },
];

const COUNTRY_CONTINENT = {
  'Afghanistan':'Asia','Bangladesh':'Asia','Cambodia':'Asia','China':'Asia','Hong Kong':'Asia',
  'India':'Asia','Indonesia':'Asia','Japan':'Asia','Malaysia':'Asia','Myanmar':'Asia',
  'Nepal':'Asia','Pakistan':'Asia','Philippines':'Asia','Singapore':'Asia',
  'South Korea':'Asia','Sri Lanka':'Asia','Taiwan':'Asia','Thailand':'Asia','Vietnam':'Asia',
  'Laos':'Asia','Bhutan':'Asia','Mongolia':'Asia','Brunei':'Asia','Timor-Leste':'Asia',
  'Australia':'Oceania','New Zealand':'Oceania','Fiji':'Oceania','Papua New Guinea':'Oceania',
  'Albania':'Europe','Austria':'Europe','Belgium':'Europe','Bosnia and Herzegovina':'Europe',
  'Bulgaria':'Europe','Croatia':'Europe','Cyprus':'Europe','Czech Republic':'Europe',
  'Denmark':'Europe','Estonia':'Europe','Finland':'Europe','France':'Europe',
  'Germany':'Europe','Greece':'Europe','Hungary':'Europe','Iceland':'Europe',
  'Ireland':'Europe','Italy':'Europe','Kosovo':'Europe','Latvia':'Europe',
  'Lithuania':'Europe','Luxembourg':'Europe','Malta':'Europe','Montenegro':'Europe',
  'Netherlands':'Europe','North Macedonia':'Europe','Norway':'Europe','Poland':'Europe',
  'Portugal':'Europe','Romania':'Europe','Russia':'Europe','Serbia':'Europe',
  'Slovakia':'Europe','Slovenia':'Europe','Spain':'Europe','Sweden':'Europe',
  'Switzerland':'Europe','Turkey':'Europe','Ukraine':'Europe','United Kingdom':'Europe',
  'Algeria':'Africa','Angola':'Africa','Cameroon':'Africa','Egypt':'Africa',
  'Ethiopia':'Africa','Ghana':'Africa','Ivory Coast':'Africa','Kenya':'Africa',
  'Libya':'Africa','Morocco':'Africa','Mozambique':'Africa','Nigeria':'Africa',
  'Rwanda':'Africa','Senegal':'Africa','South Africa':'Africa','Sudan':'Africa',
  'Tanzania':'Africa','Tunisia':'Africa','Uganda':'Africa','Zimbabwe':'Africa',
  'Argentina':'Americas','Bolivia':'Americas','Brazil':'Americas','Canada':'Americas',
  'Chile':'Americas','Colombia':'Americas','Costa Rica':'Americas','Cuba':'Americas',
  'Dominican Republic':'Americas','Ecuador':'Americas','El Salvador':'Americas',
  'Guatemala':'Americas','Haiti':'Americas','Honduras':'Americas','Jamaica':'Americas',
  'Mexico':'Americas','Nicaragua':'Americas','Panama':'Americas','Paraguay':'Americas',
  'Peru':'Americas','Puerto Rico':'Americas','Trinidad and Tobago':'Americas',
  'United States':'Americas','Uruguay':'Americas','Venezuela':'Americas',
  'Bahrain':'Middle East','Iran':'Middle East','Iraq':'Middle East','Israel':'Middle East',
  'Jordan':'Middle East','Kuwait':'Middle East','Lebanon':'Middle East','Oman':'Middle East',
  'Palestine':'Middle East','Qatar':'Middle East','Saudi Arabia':'Middle East',
  'UAE':'Middle East','United Arab Emirates':'Middle East','Yemen':'Middle East',
};

const CONTINENTS = ['Africa','Americas','Asia','Europe','Middle East','Oceania'];

// ── Hooks ────────────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mob, setMob] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

// ── Trigram similarity ───────────────────────────────────────────────────────
function trigrams(s) {
  const t = (s || '').toLowerCase()
    .replace(/\b(st|str|rd|ave|blvd|dr|ln|ct|pl)\b/g, m =>
      ({ st:'street', str:'street', rd:'road', ave:'avenue',
         blvd:'boulevard', dr:'drive', ln:'lane', ct:'court', pl:'place' })[m] || m)
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const set = new Set();
  for (let i = 0; i <= t.length - 3; i++) set.add(t.slice(i, i + 3));
  return set;
}

function similarity(a, b) {
  const ta = trigrams(a);
  const tb = trigrams(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  ta.forEach(t => { if (tb.has(t)) inter++; });
  return inter / (ta.size + tb.size - inter);
}

// ── Completeness score ───────────────────────────────────────────────────────
function completenessScore(r) {
  return ALL_FIELDS.filter(
    f => r[f] !== null && r[f] !== undefined && r[f] !== ''
  ).length;
}

// ── Union-Find duplicate grouping ────────────────────────────────────────────
function computeGroups(rows, threshold) {
  const n = rows.length;
  if (n < 2) return [];
  const parent = Array.from({ length: n }, (_, i) => i);
  function find(x) {
    while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
    return x;
  }
  // Group by city bucket for O(n) instead of O(n²) across large datasets
  const cityBuckets = {};
  rows.forEach((r, i) => {
    const k = (r.city || '__nocity__').toLowerCase();
    if (!cityBuckets[k]) cityBuckets[k] = [];
    cityBuckets[k].push(i);
  });
  const pairScores = {};
  Object.values(cityBuckets).forEach(bucket => {
    const b = bucket;
    for (let ai = 0; ai < b.length; ai++) {
      for (let bi = ai + 1; bi < b.length; bi++) {
        const i = b[ai], j = b[bi];
        const key = (rows[i].name || '') + ' ' + (rows[i].address || '');
        const key2 = (rows[j].name || '') + ' ' + (rows[j].address || '');
        const sim = similarity(key, key2);
        if (sim >= threshold) {
          const pk = i + '_' + j;
          pairScores[pk] = sim;
          parent[find(i)] = find(j);
        }
      }
    }
  });
  const groupMap = {};
  for (let i = 0; i < n; i++) {
    const root = find(i);
    if (!groupMap[root]) groupMap[root] = [];
    groupMap[root].push(i);
  }
  return Object.values(groupMap)
    .filter(g => g.length > 1)
    .map(indices => {
      let maxSim = 0;
      for (let a = 0; a < indices.length; a++) {
        for (let b = a + 1; b < indices.length; b++) {
          const pk = indices[a] + '_' + indices[b];
          if (pairScores[pk] > maxSim) maxSim = pairScores[pk];
        }
      }
      const members = indices.map(i => ({
        ...rows[i],
        _score: completenessScore(rows[i]),
      })).sort((a, b) => b._score - a._score);
      return { members, sim: maxSim, id: indices[0] };
    })
    .sort((a, b) => b.sim - a.sim);
}

// ── ConfirmBulkModal ─────────────────────────────────────────────────────────
function ConfirmBulkModal({ count, affected, onCancel, onConfirm }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(10,22,32,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontFamily: F }}>
      <div style={{ background: t.panel, borderRadius: 14, padding: '24px 28px',
        width: 380, boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>
        <div style={{ fontSize: '.95rem', fontWeight: 600, color: '#0f1e28', marginBottom: 10 }}>
          Delete duplicates in {count} group{count !== 1 ? 's' : ''}?
        </div>
        <div style={{ fontSize: '.82rem', color: '#6b8a99', marginBottom: 22, lineHeight: 1.7 }}>
          This will permanently delete{' '}
          <strong style={{ color: '#e05c5c' }}>{affected} record{affected !== 1 ? 's' : ''}</strong>,
          keeping the most complete record from each group. This cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel}
            style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid #dde4ea',
              background: t.panel, color: '#314F5C', fontSize: '.8rem', cursor: 'pointer',
              fontFamily: F }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ padding: '7px 16px', borderRadius: 7, border: 'none',
              background: '#e05c5c', color: '#fff', fontSize: '.8rem',
              fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
            Delete {affected} record{affected !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MergeModal ───────────────────────────────────────────────────────────────
function MergeModal({ group, onCancel, onSave }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const initPicks = {};
  MERGE_FIELDS.forEach(({ key }) => {
    const vals = [...new Set(
      group.members.map(r => r[key]).filter(v => v !== null && v !== undefined && v !== '')
    )];
    initPicks[key] = vals.length > 0 ? vals[0] : '';
  });
  const [picks, setPicks] = useState(initPicks);

  const activeFields = MERGE_FIELDS.filter(({ key }) => {
    const vals = [...new Set(
      group.members.map(r => r[key]).filter(v => v !== null && v !== undefined && v !== '')
    )];
    return vals.length > 0;
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(10,22,32,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontFamily: F }}>
      <div style={{ background: t.panel, borderRadius: 14, padding: '24px 28px',
        width: 540, maxHeight: '82vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '.95rem', fontWeight: 600, color: '#0f1e28' }}>
            Merge {group.members.length} records
          </div>
          <button onClick={onCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              color: '#9eb8c8', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
        {activeFields.map(({ key, label }) => {
          const vals = [...new Set(
            group.members.map(r => r[key])
              .filter(v => v !== null && v !== undefined && v !== '')
          )];
          if (vals.length === 0) return null;
          return (
            <div key={key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: '.62rem', color: '#6F8FA7', textTransform: 'uppercase',
                letterSpacing: '.06em', fontWeight: 600, marginBottom: 6 }}>
                {label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {vals.map(v => {
                  const vStr = String(v);
                  const sel = picks[key] === v;
                  return (
                    <button key={vStr}
                      onClick={() => setPicks(p => ({ ...p, [key]: v }))}
                      style={{ padding: '4px 11px', borderRadius: 999,
                        fontSize: '.78rem', cursor: 'pointer', fontFamily: F,
                        background: sel ? '#314F5C' : '#f0f4f6',
                        color: sel ? '#e0f5fc' : '#314F5C',
                        border: sel ? '1px solid #314F5C' : '1px solid #dde4ea',
                        transition: 'all .1s' }}>
                      {vStr}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end',
          marginTop: 22, paddingTop: 16, borderTop: '1px solid #f0f2f4' }}>
          <button onClick={onCancel}
            style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid #dde4ea',
              background: t.panel, color: '#314F5C', fontSize: '.8rem',
              cursor: 'pointer', fontFamily: F }}>Cancel</button>
          <button onClick={() => onSave(picks)}
            style={{ padding: '7px 16px', borderRadius: 7, border: 'none',
              background: t.accent, color: '#e0f5fc', fontSize: '.8rem',
              fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
            Save merged record
          </button>
        </div>
      </div>
    </div>
  );
}

// ── GroupCard ────────────────────────────────────────────────────────────────
function GroupCard({ group, groupIdx, selected, onSelect, onKeepBest, onMerge, onSkip, saving }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const best = group.members[0];
  const pct = Math.round(group.sim * 100);
  const pillColor = pct >= 85 ? '#fce8e8' : '#fef3c7';
  const pillText = pct >= 85 ? '#7a2020' : '#92400e';

  const diffFields = COMPARE_FIELDS.filter(({ key }) => {
    const vals = new Set(
      group.members.map(r => String(r[key] ?? '').trim().toLowerCase())
    );
    return vals.size > 1;
  });
  const sameFields = COMPARE_FIELDS.filter(({ key }) => {
    const vals = new Set(
      group.members.map(r => String(r[key] ?? '').trim().toLowerCase())
    );
    return vals.size === 1 && group.members[0][key];
  });
  const displayFields = [
    ...diffFields,
    ...sameFields.slice(0, 3),
    { key: '_score', label: 'Completeness' },
  ];

  return (
    <div style={{ background: t.panel, borderRadius: 12,
      border: selected
        ? '1.5px solid #314F5C'
        : '0.5px solid rgba(15,30,40,0.1)',
      marginBottom: 12, overflow: 'hidden',
      boxShadow: selected
        ? '0 0 0 3px rgba(49,79,92,0.12)'
        : '0 1px 3px rgba(15,30,40,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 14px', background: '#f8fafb',
        borderBottom: '0.5px solid rgba(15,30,40,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            onClick={() => onSelect(groupIdx)}
            style={{ width: 15, height: 15, borderRadius: 3, flexShrink: 0,
              border: selected ? 'none' : '1px solid #c0cdd5',
              background: selected ? '#314F5C' : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer' }}>
            {selected && (
              <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="#e0f5fc" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span style={{ fontSize: '.78rem', fontWeight: 600, color: '#0f1e28' }}>
            Group {groupIdx + 1}
          </span>
          <span style={{ fontSize: '.72rem', color: '#9eb8c8' }}>
            · {group.members.length} records
          </span>
          <span style={{ fontSize: '.68rem', padding: '2px 7px', borderRadius: 999,
            background: pillColor, color: pillText, fontWeight: 600 }}>
            {pct}% match
          </span>
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {saving ? (
            <span style={{ fontSize: '.72rem', color: '#6F8FA7' }}>Saving…</span>
          ) : (
            <>
              <button onClick={() => onKeepBest(group)}
                style={{ padding: '4px 11px', borderRadius: 6, border: 'none',
                  background: t.accent, color: '#e0f5fc', fontSize: '.72rem',
                  fontWeight: 500, cursor: 'pointer', fontFamily: F }}>
                Keep best
              </button>
              <button onClick={() => onMerge(group)}
                style={{ padding: '4px 10px', borderRadius: 6,
                  border: '0.5px solid #c0cdd5', background: t.panel,
                  color: '#6F8FA7', fontSize: '.72rem', cursor: 'pointer',
                  fontFamily: F }}>
                Merge
              </button>
              <button onClick={() => onSkip(groupIdx)}
                style={{ padding: '4px 8px', borderRadius: 6, border: 'none',
                  background: 'none', color: '#9eb8c8', fontSize: '.72rem',
                  cursor: 'pointer', fontFamily: F }}>
                Skip
              </button>
            </>
          )}
        </div>
      </div>
      {/* Comparison table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse',
          fontSize: '11.5px', fontFamily: F }}>
          <thead>
            <tr>
              <th style={{ padding: '7px 12px', textAlign: 'left',
                color: '#9eb8c8', fontWeight: 500, fontSize: '11px',
                background: '#f8fafb', width: 110, whiteSpace: 'nowrap' }}>
                Field
              </th>
              {group.members.map((m, mi) => (
                <th key={m.ID} style={{ padding: '7px 12px', textAlign: 'left',
                  fontSize: '11px', background: '#f8fafb',
                  color: mi === 0 ? '#314F5C' : '#9eb8c8', fontWeight: 500,
                  whiteSpace: 'nowrap' }}>
                  #{m.ID}
                  {mi === 0 && (
                    <span style={{ marginLeft: 5, fontSize: '9px', fontWeight: 700,
                      color: '#fff', background: '#2da06a', padding: '1px 5px',
                      borderRadius: 999 }}>BEST</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayFields.map(({ key, label }) => {
              if (key === '_score') {
                return (
                  <tr key="_score" style={{ background: '#f8fafb' }}>
                    <td style={{ padding: '6px 12px', color: '#9eb8c8',
                      fontSize: '11px', whiteSpace: 'nowrap',
                      borderTop: '0.5px solid rgba(15,30,40,0.07)' }}>
                      Completeness
                    </td>
                    {group.members.map((m, mi) => {
                      const s = m._score;
                      const pctBar = Math.round((s / ALL_FIELDS.length) * 100);
                      const barColor = pctBar >= 70 ? '#2da06a' : pctBar >= 40 ? '#d97706' : '#e05c5c';
                      return (
                        <td key={m.ID}
                          style={{ padding: '6px 12px',
                            borderTop: '0.5px solid rgba(15,30,40,0.07)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 60, height: 4, background: '#edf6fb',
                              borderRadius: 999, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: pctBar + '%',
                                background: barColor, borderRadius: 999 }} />
                            </div>
                            <span style={{ fontSize: '11px', color: '#6b8a99' }}>
                              {s}/{ALL_FIELDS.length}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              }
              const vals = group.members.map(r => r[key]);
              const isDiff = new Set(vals.map(v => String(v ?? '').trim().toLowerCase())).size > 1;
              return (
                <tr key={key} style={{ background: isDiff ? '#fffbf4' : '#fff' }}>
                  <td style={{ padding: '6px 12px', color: '#9eb8c8',
                    fontSize: '11px', whiteSpace: 'nowrap',
                    borderTop: '0.5px solid rgba(15,30,40,0.07)' }}>
                    {label}
                  </td>
                  {vals.map((v, vi) => (
                    <td key={vi}
                      style={{ padding: '6px 12px', color: v ? '#0f1e28' : '#c8d8e0',
                        fontStyle: v ? 'normal' : 'italic',
                        borderTop: '0.5px solid rgba(15,30,40,0.07)',
                        maxWidth: 220, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v != null && v !== '' ? String(v) : '—'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────
function DuplicatesBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [continent, setContinent] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [allCountries, setAllCountries] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [mergeTarget, setMergeTarget] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [savingSet, setSavingSet] = useState(new Set());
  const [error, setError] = useState('');

  // Load distinct countries on mount
  useEffect(() => {
    async function loadCountries() {
      const { data, error: _e } = await supabase
        .from('Locations')
        .select('country')
        .not('country', 'is', null)
        .order('country');
      if (data) {
        const unique = [...new Set(data.map(r => r.country).filter(Boolean))];
        setAllCountries(unique);
      }
    }
    loadCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (!country) { setAllCities([]); setCity(''); return; }
    async function loadCities() {
      const { data, error: _e } = await supabase
        .from('Locations')
        .select('city')
        .eq('country', country)
        .not('city', 'is', null)
        .order('city');
      if (data) {
        const unique = [...new Set(data.map(r => r.city).filter(Boolean))];
        setAllCities(unique);
      }
    }
    loadCities();
  }, [country]);

  const runScan = useCallback(async () => {
    if (!country) return;
    setScanning(true);
    setGroups([]);
    setSelected(new Set());
    setSkipped(new Set());
    setError('');
    setTotal(0);
    try {
      let query = supabase
        .from('Locations')
        .select(
          'ID,name,country_code,country,city,address,phone,type,tags,' +
          'website,booking_url,specialties_url,telehealth,er_24_7,' +
          'languages,latitude,longitude,last_verified,notes'
        )
        .eq('country', country);
      if (city) query = query.eq('city', city);

      let allRows = [];
      let from = 0;
      const pageSize = 1000;
      let done = false;
      while (!done) {
        setScanMsg('Fetching records ' + (allRows.length + 1).toLocaleString() + '…');
        const { data, error: _e } = await query.range(from, from + pageSize - 1);
        if (_e || !data) { done = true; break; }
        allRows = allRows.concat(data);
        if (data.length < pageSize) done = true;
        else from += pageSize;
      }
      setTotal(allRows.length);
      setScanMsg('Comparing ' + allRows.length.toLocaleString() + ' records…');
      await new Promise(r => setTimeout(r, 60));
      const found = computeGroups(allRows, 0.5);
      setGroups(found);
    } catch (_e) {
      setError('Scan failed. Please try again.');
    }
    setScanning(false);
    setScanMsg('');
  }, [country, city]);

  // Auto-scan when country or city changes
  useEffect(() => {
    if (!country) return;
    const t = setTimeout(() => { runScan(); }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, city]);

  function toggleSelect(idx) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  function toggleSelectAll() {
    const visible = groups
      .map((_, i) => i)
      .filter(i => !skipped.has(i));
    const allSel = visible.every(i => selected.has(i));
    if (allSel) setSelected(new Set());
    else setSelected(new Set(visible));
  }

  async function deleteIds(ids) {
    const batchSize = 80;
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      try { await supabase.from('Locations').delete().in('id', batch); } catch (_e) {}
    }
  }

  async function handleKeepBest(group, groupIdx) {
    const best = group.members[0];
    const toDelete = group.members.slice(1).map(r => r.ID);
    setSavingSet(prev => new Set([...prev, groupIdx]));
    await deleteIds(toDelete);
    setGroups(prev => prev.filter((_, i) => i !== groupIdx));
    setSelected(prev => { const n = new Set(prev); n.delete(groupIdx); return n; });
    setSavingSet(prev => { const n = new Set(prev); n.delete(groupIdx); return n; });
  }

  async function handleMergeSave(group, groupIdx, picks) {
    const best = group.members[0];
    const toDelete = group.members.slice(1).map(r => r.ID);
    const updatePayload = {};
    MERGE_FIELDS.forEach(({ key }) => {
      if (picks[key] !== undefined && picks[key] !== '') updatePayload[key] = picks[key];
    });
    setSavingSet(prev => new Set([...prev, groupIdx]));
    setMergeTarget(null);
    try { await supabase.from('Locations').update(updatePayload).eq('id', best.id); } catch (_e) {}
    await deleteIds(toDelete);
    setGroups(prev => prev.filter((_, i) => i !== groupIdx));
    setSelected(prev => { const n = new Set(prev); n.delete(groupIdx); return n; });
    setSavingSet(prev => { const n = new Set(prev); n.delete(groupIdx); return n; });
  }

  async function handleBulkDelete() {
    const toProcess = [...selected].filter(i => !skipped.has(i));
    const toDeleteAll = [];
    toProcess.forEach(i => {
      const g = groups[i];
      if (!g) return;
      g.members.slice(1).forEach(r => toDeleteAll.push(r.ID));
    });
    setConfirmBulk(false);
    setSavingSet(new Set(toProcess));
    await deleteIds(toDeleteAll);
    setGroups(prev => prev.filter((_, i) => !toProcess.includes(i)));
    setSelected(new Set());
    setSavingSet(new Set());
  }

  // Derived counts
  const visibleGroups = groups.filter((_, i) => !skipped.has(i));
  const selectedVisible = [...selected].filter(i => !skipped.has(i));
  const bulkAffected = selectedVisible.reduce((acc, i) => {
    return acc + ((groups[i]?.members.length ?? 1) - 1);
  }, 0);
  const allSelected = visibleGroups.length > 0 &&
    visibleGroups.every((_, ri) => {
      const realIdx = groups.indexOf(visibleGroups[ri]);
      return selected.has(realIdx);
    });

  const filteredCountries = allCountries.filter(c =>
    !continent || (COUNTRY_CONTINENT[c] === continent) || continent === 'Other'
  );

  const selStyle = {
    padding: '7px 10px', borderRadius: 7,
    border: '0.5px solid rgba(15,30,40,0.15)',
    background: t.panel, color: '#0f1e28',
    fontSize: isMobile ? '.78rem' : '.8rem',
    fontFamily: F, cursor: 'pointer',
    minWidth: isMobile ? 100 : 130,
    appearance: 'none', WebkitAppearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 10 10\'%3E%3Cpath d=\'M2 3.5L5 6.5L8 3.5\' stroke=\'%239eb8c8\' strokeWidth=\'1.2\' strokeLinecap=\'round\' strokeLinejoin=\'round\' fill=\'none\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    paddingRight: 26,
  };

  return (
    <div style={{ fontFamily: F }}>
      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16,
        alignItems: 'center' }}>
        <select value={continent} onChange={e => { setContinent(e.target.value); setCountry(''); }}
          style={selStyle}>
          <option value="">All continents</option>
          {CONTINENTS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: '#c0cdd5', flexShrink: 0 }}>
          <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <select value={country} onChange={e => { setCountry(e.target.value); setCity(''); }}
          style={{ ...selStyle, minWidth: isMobile ? 120 : 150 }}>
          <option value="">Select country</option>
          {filteredCountries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: '#c0cdd5', flexShrink: 0 }}>
          <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <select value={city} onChange={e => setCity(e.target.value)}
          style={{ ...selStyle, minWidth: isMobile ? 110 : 140 }}
          disabled={!country}>
          <option value="">All cities</option>
          {allCities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {country && (
          <span style={{ fontSize: '.72rem', color: '#6F8FA7',
            background: 'rgba(111,143,167,0.1)', border: '0.5px solid rgba(111,143,167,0.25)',
            padding: '3px 9px', borderRadius: 999 }}>
            📍 {[country, city].filter(Boolean).join(' › ')}
          </span>
        )}
        <button onClick={runScan} disabled={!country || scanning}
          style={{ display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8,
            background: country ? '#314F5C' : '#c0cdd5',
            color: '#e0f5fc', border: 'none',
            fontSize: '.78rem', fontWeight: 500, cursor: country ? 'pointer' : 'not-allowed',
            fontFamily: F, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
            strokeLinejoin="round" style={{ animation: scanning ? 'spin 1s linear infinite' : 'none' }}>
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
          {scanning ? 'Scanning…' : 'Re-scan'}
        </button>
      </div>

      {/* Metrics */}
      {(groups.length > 0 || total > 0) && !scanning && (
        <div style={{ display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Duplicate groups', value: visibleGroups.length, color: visibleGroups.length > 0 ? '#e05c5c' : '#2da06a' },
            { label: 'Records to remove', value: visibleGroups.reduce((a, g) => a + g.members.length - 1, 0), color: '#d97706' },
            { label: 'Scanned', value: total.toLocaleString(), color: '#314F5C' },
            { label: 'Skipped', value: skipped.size, color: '#6F8FA7' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: t.panel, borderRadius: 10,
              border: '0.5px solid rgba(15,30,40,0.08)', padding: '12px 14px',
              boxShadow: '0 1px 3px rgba(15,30,40,0.05)' }}>
              <div style={{ fontSize: '11px', color: '#9eb8c8', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 500, color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Scan status */}
      {scanning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', background: '#f0f8ff',
          border: '0.5px solid #b8dcf0', borderRadius: 10, marginBottom: 16 }}>
          <div style={{ width: 16, height: 16, border: '2px solid #314F5C',
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          <span style={{ fontSize: '.82rem', color: '#314F5C' }}>
            {scanMsg || 'Scanning…'}
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '11px 14px', background: '#fce8e8',
          border: '0.5px solid #f0b0b0', borderRadius: 9,
          fontSize: '.82rem', color: '#7a2020', marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* Empty state — no country selected */}
      {!country && !scanning && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9eb8c8' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
            style={{ marginBottom: 14, opacity: 0.5 }}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <div style={{ fontSize: '.88rem', fontWeight: 500, color: '#6F8FA7', marginBottom: 6 }}>
            Select a country to start scanning
          </div>
          <div style={{ fontSize: '.78rem' }}>
            Duplicates are detected by fuzzy name + address match within each city
          </div>
        </div>
      )}

      {/* Empty result */}
      {country && !scanning && groups.length === 0 && total > 0 && (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#9eb8c8' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
            style={{ marginBottom: 12, color: '#2da06a', opacity: 0.7 }}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <div style={{ fontSize: '.88rem', fontWeight: 500, color: '#2da06a', marginBottom: 6 }}>
            No duplicates found
          </div>
          <div style={{ fontSize: '.78rem' }}>
            {total.toLocaleString()} records scanned in{' '}
            {[country, city].filter(Boolean).join(', ')}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {visibleGroups.length > 0 && !scanning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 13px', borderRadius: 9, marginBottom: 14,
          background: selectedVisible.length > 0 ? '#fff8f0' : '#f8fafb',
          border: selectedVisible.length > 0
            ? '0.5px solid #f0c080' : '0.5px solid rgba(15,30,40,0.09)',
          flexWrap: 'wrap' }}>
          <div
            onClick={toggleSelectAll}
            style={{ width: 15, height: 15, borderRadius: 3, flexShrink: 0,
              border: allSelected ? 'none' : '1px solid #c0cdd5',
              background: allSelected ? '#314F5C' : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer' }}>
            {allSelected && (
              <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="#e0f5fc" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span style={{ fontSize: '.78rem', color: '#314F5C', fontWeight: 500 }}>
            {selectedVisible.length === 0
              ? visibleGroups.length + ' group' + (visibleGroups.length !== 1 ? 's' : '')
              : selectedVisible.length + ' of ' + visibleGroups.length + ' selected'}
          </span>
          {selectedVisible.length > 0 && (
            <>
              <div style={{ width: 1, height: 14, background: 'rgba(15,30,40,0.12)' }} />
              <button onClick={() => setConfirmBulk(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', borderRadius: 6, background: '#e05c5c',
                  color: '#fff', border: 'none', fontSize: '.75rem',
                  fontWeight: 500, cursor: 'pointer', fontFamily: F }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                </svg>
                Delete {bulkAffected} duplicate{bulkAffected !== 1 ? 's' : ''} · keep best
              </button>
              <span onClick={() => setSelected(new Set())}
                style={{ fontSize: '.72rem', color: '#9eb8c8', cursor: 'pointer' }}>
                Clear
              </span>
            </>
          )}
        </div>
      )}

      {/* Group cards */}
      {!scanning && groups.map((group, groupIdx) => {
        if (skipped.has(groupIdx)) return null;
        return (
          <GroupCard
            key={group.id + '_' + groupIdx}
            group={group}
            groupIdx={groupIdx}
            selected={selected.has(groupIdx)}
            onSelect={toggleSelect}
            onKeepBest={g => handleKeepBest(g, groupIdx)}
            onMerge={g => setMergeTarget({ group: g, groupIdx })}
            onSkip={i => setSkipped(prev => new Set([...prev, i]))}
            saving={savingSet.has(groupIdx)}
          />
        );
      })}

      {/* Merge modal */}
      {mergeTarget && (
        <MergeModal
          group={mergeTarget.group}
          onCancel={() => setMergeTarget(null)}
          onSave={picks => handleMergeSave(mergeTarget.group, mergeTarget.groupIdx, picks)}
        />
      )}

      {/* Confirm bulk delete */}
      {confirmBulk && (
        <ConfirmBulkModal
          count={selectedVisible.length}
          affected={bulkAffected}
          onCancel={() => setConfirmBulk(false)}
          onConfirm={handleBulkDelete}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default function Duplicates(props) {
  if (props && props.embed) return <DuplicatesBody {...props} />;
  return <ConsoleShell title="Duplicates"><DuplicatesBody {...props} /></ConsoleShell>;
}
